/**
 * The account's people as radio choices in "Send this to someone else"
 * (proposal 7, kanban 0da0c669): an empty circle until chosen, a ticked one
 * once chosen. The shaded box an unavailable role used to sit in read as
 * picked when it was not; an unavailable role now says why under a dashed,
 * plain box. Choosing the chosen one again clears it.
 */
import type { DoorRoleTargetRead, RouteRole } from '../../services/qcpInvestigations';
import { trackEvent } from '../../services/portalTracking';
import { roleChipHint, roleChipText } from './routeTargetsView';
import { pickRole } from './routePickView';
import styles from './RoutePicker.module.css';

export function RouteRoleChoices({
  roles,
  selected,
  onChange,
  disabled,
  threadNo,
}: {
  roles: DoorRoleTargetRead[];
  selected: ReadonlySet<RouteRole>;
  onChange: (next: Set<RouteRole>) => void;
  disabled: boolean;
  threadNo: string;
}) {
  if (roles.length === 0) return null;
  return (
    <div className={styles.roleChoices} role="radiogroup" aria-label="Route to a role">
      {roles.map((r) => {
        const on = selected.has(r.role);
        const hint = roleChipHint(r);
        return (
          <button
            key={r.role}
            type="button"
            role="radio"
            aria-checked={on}
            aria-label={roleChipText(r)}
            className={styles.roleChoice}
            data-state={!r.available ? 'off' : on ? 'on' : 'idle'}
            title={hint ?? undefined}
            disabled={disabled || !r.available}
            onClick={() => {
              onChange(pickRole(selected, r.role));
              trackEvent('action', 'qcp_route_role_pick', {
                thread_no: threadNo,
                role: r.role,
                selected: !on,
              });
            }}
          >
            <span className={styles.radioDot} data-on={on || undefined} aria-hidden="true">
              {on ? '✓' : ''}
            </span>
            <span className={styles.roleChoiceBody}>
              <span className={styles.roleChoiceText}>{roleChipText(r)}</span>
              {hint && <span className={styles.roleChoiceHint}>{hint}</span>}
            </span>
          </button>
        );
      })}
    </div>
  );
}

export default RouteRoleChoices;
